import { inject, Injectable, Signal, computed } from "@angular/core";
import { Observable, of, tap } from "rxjs";
import { CachedServiceBase } from "../../base/cached-service.base";
import { ResponsePsychoDto, UpdatePsychoDto } from "../../../api/models";
import { PsychologistsApiService } from "../../api/users/psychologists-api.service";

@Injectable({
  providedIn: "root",
})
export class CachedPsychologistsService extends CachedServiceBase<ResponsePsychoDto> {
  /** The psychologists API service */
  private readonly _psychologistsApiService = inject(PsychologistsApiService);

  // =============================================================================
  // IMPLEMENT ABSTRACT METHODS FROM BASE CLASS
  // =============================================================================

  /**
   * Get filtered data - for psychologists, just return all data
   */
  protected getFilteredData(): Signal<ResponsePsychoDto[]> {
    return computed(() => this.data());
  }

  /**
   * Refresh data - currently no-op as psychologists are loaded on demand
   */
  public refresh(): void {
    // No-op for now - psychologists are loaded individually
    // Could implement a bulk fetch if needed in future
  }

  /**
   * Reset the service state
   */
  public reset(): void {
    this.clearData();
  }

  // =============================================================================
  // SERVICE METHODS
  // =============================================================================

  /**
   * Find a psychologist by ID
   * @param id the psychologist ID
   * @returns Observable with the psychologist data
   */
  public findOne$(id: string): Observable<ResponsePsychoDto> {
    
    // Check cache first
    const cached = this.data().find(psycho => psycho.id === id);
    if (cached) {
      return of(cached);
    }

    // Fetch from backend and cache
    return this._psychologistsApiService.findOne$(id).pipe(
      tap({
        next: (psycho) => {
          // Add to cache
          const current = this.data();
          this.updateData([...current, psycho]);
        },
        error: (error) => {
          console.error("❌ [CachedPsychologistsService] API error:", {
            status: error.status,
            message: error.message,
            url: error.url,
            requestedId: id
          });
        }
      })
    );
  }

  /**
   * Update a psychologist
   * @param id the psychologist ID
   * @param updateDto the update data
   * @returns Observable with the updated psychologist
   */
  public update$(
    id: string,
    updateDto: UpdatePsychoDto,
  ): Observable<ResponsePsychoDto> {
    return this._psychologistsApiService.update$(id, updateDto).pipe(
      tap(updated => {
        // Update cache
        const current = this.data();
        const index = current.findIndex(p => p.id === id);
        if (index !== -1) {
          const newData = [...current];
          newData[index] = updated;
          this.updateData(newData);
        }
      })
    );
  }
}